import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Loader2, Mail, Send, Eye, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { showCreditReceipt } from "../../lib/creditReceipt";

/**
 * Diálogo para pedir presupuesto por email a un proveedor alternativo.
 *
 * Flujo:
 *   1. Email + nota opcional → Preview
 *   2. Preview muestra subject + body generado por IA
 *   3. Botón Enviar → POST con preview_only=false
 *
 * Coste: 2 créditos al generar el preview. El envío posterior NO cobra otro
 * preview porque el endpoint backend siempre genera + cobra. Para evitar
 * doble cobro, hacemos UN solo POST con preview_only=false directamente
 * cuando el usuario pulsa "Enviar". El "Ver preview" hace POST con preview_only=true.
 */
export default function QuoteRequestDialog({
  open, onOpenChange, alternative, badSupplierId, restaurantId,
}) {
  const [email, setEmail] = useState("");
  const [extraNote, setExtraNote] = useState("");
  const [preview, setPreview] = useState(null); // {subject, body_html, body_text}
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);

  const reset = () => {
    setEmail("");
    setExtraNote("");
    setPreview(null);
  };

  const handleClose = (o) => {
    if (sending || loadingPreview) return;
    if (!o) reset();
    onOpenChange(o);
  };

  const validateEmail = () => {
    const v = (email || "").trim();
    if (!v.includes("@") || v.length < 5) {
      toast.error("Email inválido");
      return null;
    }
    return v;
  };

  const handlePreview = async () => {
    const v = validateEmail();
    if (!v) return;
    setLoadingPreview(true);
    try {
      const res = await api.request(
        `/restaurants/${restaurantId}/suppliers/quote-request`,
        {
          method: "POST",
          body: JSON.stringify({
            target_email: v,
            target_supplier_name: alternative?.name || "Proveedor",
            target_supplier_type: alternative?.type || null,
            bad_supplier_id: badSupplierId || null,
            extra_note: extraNote.trim() || null,
            preview_only: true,
          }),
        },
      );
      setPreview(res);
      if (res._credits_receipt) showCreditReceipt(res._credits_receipt);
    } catch (e) {
      toast.error(e?.message || "Error generando el email");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    const v = validateEmail();
    if (!v) return;
    setSending(true);
    try {
      const body = {
        target_email: v,
        target_supplier_name: alternative?.name || "Proveedor",
        target_supplier_type: alternative?.type || null,
        bad_supplier_id: badSupplierId || null,
        extra_note: extraNote.trim() || null,
        preview_only: false,
      };
      // If we already have a preview, reuse it to avoid double-charging credits.
      if (preview?.subject && preview?.body_html) {
        body.precomputed_subject = preview.subject;
        body.precomputed_body_html = preview.body_html;
      }
      const res = await api.request(
        `/restaurants/${restaurantId}/suppliers/quote-request`,
        { method: "POST", body: JSON.stringify(body) },
      );
      if (res._credits_receipt) showCreditReceipt(res._credits_receipt);
      toast.success(`Email enviado a ${res.to}`);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="quote-request-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-violet-600" />
            Pedir presupuesto a {alternative?.name}
          </DialogTitle>
          <DialogDescription>
            La IA redactará un email profesional con los ingredientes y volúmenes basados en tu
            histórico real. Coste: 2 créditos por email generado (sólo se cobra una vez por sesión).
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="quote-email" className="text-sm font-medium">
                Email del proveedor *
              </Label>
              <Input
                id="quote-email"
                type="email"
                placeholder="ventas@proveedor.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loadingPreview || sending}
                data-testid="quote-email-input"
              />
              <p className="text-xs text-muted-foreground">
                {alternative?.contact_hint || "Búscalo en su web. Recibirás copia (CC) automáticamente."}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="quote-note" className="text-sm font-medium">
                Nota extra (opcional)
              </Label>
              <Textarea
                id="quote-note"
                placeholder="Ej: necesitamos entrega los lunes, somos un restaurante pequeño…"
                value={extraNote}
                onChange={(e) => setExtraNote(e.target.value)}
                maxLength={500}
                rows={2}
                disabled={loadingPreview || sending}
                data-testid="quote-note-input"
              />
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-3" data-testid="quote-preview">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
              Preview generado por IA. Revisa el contenido antes de enviar.
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted/30 px-3 py-2 border-b">
                <p className="text-xs text-muted-foreground">Asunto</p>
                <p className="text-sm font-medium">{preview.subject}</p>
              </div>
              <div className="bg-muted/30 px-3 py-2 border-b text-xs">
                <span className="text-muted-foreground">Para: </span>
                <strong>{email}</strong>
                <span className="text-muted-foreground ml-2">+ CC a ti</span>
              </div>
              <div
                className="px-3 py-3 max-h-[40vh] overflow-y-auto text-sm prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: preview.body_html }}
              />
            </div>

            {preview._provider && (
              <p className="text-[10px] text-muted-foreground text-right">
                Generado por {preview._provider}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          {preview ? (
            <>
              <Button
                variant="outline"
                onClick={() => setPreview(null)}
                disabled={sending}
                data-testid="quote-back-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                Volver
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="quote-send-btn"
              >
                {sending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                ) : (
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                )}
                Enviar email
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={loadingPreview}
                data-testid="quote-cancel-btn"
              >
                Cancelar
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handlePreview}
                  disabled={loadingPreview || !email}
                  data-testid="quote-preview-btn"
                >
                  {loadingPreview ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Eye className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Ver preview (2 cr)
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={loadingPreview || sending || !email}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="quote-send-direct-btn"
                >
                  {sending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Enviar (2 cr)
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
